import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import Modal from '../General/Modal';
import UserSearchPicker from '../General/UserSearchPicker';
import { fetchUsers } from '../../api/users';
import { getTags } from '../../actions/tagActions';
import './SendTestModal.scss';

const MAX_TAG_OPTIONS = 50;
const searchAnnouncementUsers = async query => {
  const data = await fetchUsers({ search: query, limit: 10 });
  return data.users || [];
};

const SendTestModal = ({ isOpen, onClose, onSelectUsers }) => {
  const dispatch = useDispatch();
  const tags = useSelector(state => state.tags?.tags ?? []);

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [tagSearchInput, setTagSearchInput] = useState('');
  const [tagAddLoading, setTagAddLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTagsLoading(true);
      dispatch(getTags()).finally(() => setTagsLoading(false));
    } else {
      setSelectedUsers([]);
      setTagSearchInput('');
    }
  }, [isOpen, dispatch]);

  const allTagOptions = useMemo(
    () =>
      (tags || []).map(tag => {
        const label = tag.value || tag.name || `Tag ${tag.id}`;
        const type = tag.type || 'general';
        return { value: tag.id, label: `${label} (${type})`, tagType: type };
      }),
    [tags]
  );

  const tagOptions = useMemo(() => {
    const q = tagSearchInput.trim().toLowerCase();

    if (!q) return allTagOptions.slice(0, MAX_TAG_OPTIONS);
    return allTagOptions.filter(opt => opt.label.toLowerCase().includes(q)).slice(0, MAX_TAG_OPTIONS);
  }, [allTagOptions, tagSearchInput]);

  const handleTagSelect = async option => {
    if (!option) return;
    setTagAddLoading(true);
    try {
      const data = await fetchUsers({ tagIds: JSON.stringify([option.value]), limit: 500 });
      const users = data.users || [];
      if (users.length > 0) {
        setSelectedUsers(prev => {
          const existingIds = new Set(prev.map(u => u.id));
          const newUsers = users.filter(u => !existingIds.has(u.id));
          return [...prev, ...newUsers];
        });
      }
    } catch {
      window.ALERT.error('Failed to add users by tag.');
    } finally {
      setTagAddLoading(false);
    }
  };

  const handleConfirm = () => {
    if (selectedUsers.length > 0) {
      onSelectUsers(selectedUsers);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Modal close={onClose} title='Send Test Message' className='send-test-modal-container'>
      <div className='send-test-modal'>
        <p className='send-test-modal-description'>Search for users individually or add all users with a specific tag.</p>

        <div className='send-test-modal-tag-filter'>
          <label className='send-test-modal-label'>Add users by tag</label>
          <Select
            className='send-test-modal-tag-select'
            classNamePrefix='send-test-tag'
            placeholder='Search for a tag to add its users...'
            value={null}
            onChange={handleTagSelect}
            options={tagOptions}
            inputValue={tagSearchInput}
            onInputChange={setTagSearchInput}
            filterOption={() => true}
            isClearable
            isLoading={tagsLoading || tagAddLoading}
            noOptionsMessage={() => (tagSearchInput.trim() ? 'No matching tags' : 'Type to search tags')}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        </div>

        <UserSearchPicker
          selectedUsers={selectedUsers}
          onChange={setSelectedUsers}
          searchUsers={searchAnnouncementUsers}
          label='Search users'
          autoFocus
        />

        <div className='send-test-modal-actions'>
          <button type='button' className='send-test-modal-btn send-test-modal-btn-secondary' onClick={onClose}>
            Cancel
          </button>
          <button
            type='button'
            className='send-test-modal-btn send-test-modal-btn-primary'
            onClick={handleConfirm}
            disabled={selectedUsers.length === 0}
          >
            Send Test{selectedUsers.length > 0 ? ` (${selectedUsers.length})` : ''}
          </button>
        </div>
      </div>
    </Modal>
  );
};

SendTestModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectUsers: PropTypes.func.isRequired
};

export default SendTestModal;
